import {
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  InteractionRequiredAuthErrorCodes,
} from "@azure/msal-browser";
import { flattenErrorStrings } from "utils/b2cRedirectErrors";

/** MSAL / B2C error codes that require an interactive login after silent refresh fails. */
const INTERACTION_REQUIRED_ERROR_CODES = new Set([
  InteractionRequiredAuthErrorCodes.interactionRequired,
  InteractionRequiredAuthErrorCodes.consentRequired,
  InteractionRequiredAuthErrorCodes.loginRequired,
  InteractionRequiredAuthErrorCodes.refreshTokenExpired,
  InteractionRequiredAuthErrorCodes.noTokensFound,
  InteractionRequiredAuthErrorCodes.badToken,
  InteractionRequiredAuthErrorCodes.nativeAccountUnavailable,
  InteractionRequiredAuthErrorCodes.uiNotAllowed,
  InteractionRequiredAuthErrorCodes.interruptedUser,
]);

/**
 * Returns true when a silent MSAL token acquisition failure should fall back to
 * interactive login (redirect), e.g. after long sleep or expired refresh tokens.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isMsalInteractionRequiredError(error) {
  if (error instanceof InteractionRequiredAuthError) {
    return true;
  }

  const errorCode =
    typeof error === "object" && error !== null && "errorCode" in error
      ? String(error.errorCode)
      : "";
  if (errorCode && INTERACTION_REQUIRED_ERROR_CODES.has(errorCode)) {
    return true;
  }

  const normalized = flattenErrorStrings(error).join(" ").toLowerCase();
  return (
    normalized.includes("interactionrequiredautherror") ||
    normalized.includes("login_required") ||
    normalized.includes("interaction_required") ||
    normalized.includes("refresh_token_expired") ||
    normalized.includes("consent_required") ||
    normalized.includes("no_tokens_found")
  );
}

/**
 * Returns true when an error indicates the cached ID token is no longer valid
 * and silent renewal is unlikely to succeed without user interaction.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isMsalTokenExpirationError(error) {
  if (isMsalInteractionRequiredError(error)) {
    return true;
  }

  const normalized = flattenErrorStrings(error).join(" ").toLowerCase();
  return (
    normalized.includes("token_expired") ||
    normalized.includes("id_token_expired") ||
    normalized.includes("invalid_grant") ||
    normalized.includes("expired")
  );
}

/**
 * Returns true when a silent MSAL call timed out waiting for the hidden iframe /
 * redirect bridge (common after sleep when Tracking Prevention blocks iframe storage).
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isMsalSilentIframeTimeoutError(error) {
  const errorCode =
    typeof error === "object" && error !== null && "errorCode" in error
      ? String(error.errorCode)
      : typeof error === "object" && error !== null && "code" in error
        ? String(error.code)
        : "";

  if (
    errorCode === BrowserAuthErrorCodes.timedOut ||
    errorCode === "timed_out"
  ) {
    return true;
  }

  const normalized = flattenErrorStrings(error).join(" ").toLowerCase();
  return normalized.includes("timed_out");
}
